import React, { useEffect, useState } from 'react';
import { Select, Spin } from 'antd';
import { CustomService } from '@core/services/custom.service';
import {
  ConfigListItem,
  ConfigListResponse,
} from '@core/@models/ApiManualModel';
import { finalize } from 'rxjs/operators';
import { useAuthContext } from '@contexts/AuthProvider';

interface ConfigSelectorProps {
  type: 'download' | 'directory';
  value?: string;
  onChange: (configName: string) => void;
  onConfigSelect?: (config: ConfigListItem | null) => void;
  placeholder?: string;
}

export const ConfigSelector: React.FC<ConfigSelectorProps> = ({
  type,
  value,
  onChange,
  onConfigSelect,
  placeholder = 'Select a saved config',
}) => {
  const [loading, setLoading] = useState(false);
  const [configs, setConfigs] = useState<ConfigListItem[]>([]);
  const { roleId } = useAuthContext();

  useEffect(() => {
    fetchConfigs();
  }, [type, roleId]);

  const fetchConfigs = () => {
    const endpoint =
      type === 'download'
        ? '/download-services/config/list'
        : '/directory-download-services/config/list';

    setLoading(true);
    CustomService.getData<ConfigListResponse>(
      endpoint,
      { size: 1000 },
      { 'x-role-id': roleId }
    )
      .pipe(finalize(() => setLoading(false)))
      .subscribe({
        next: (result) => {
          setConfigs(result.content || []);
        },
        error: () => {
          setConfigs([]);
        },
      });
  };

  const handleChange = (selectedName: string) => {
    onChange(selectedName);
    if (onConfigSelect) {
      const config = configs.find((c) => c.configName === selectedName);
      onConfigSelect(config || null);
    }
  };

  const handleClear = () => {
    onChange('');
    if (onConfigSelect) {
      onConfigSelect(null);
    }
  };

  return (
    <Select
      value={value || undefined}
      onChange={handleChange}
      onClear={handleClear}
      placeholder={placeholder}
      loading={loading}
      showSearch
      allowClear
      style={{ width: '100%' }}
      optionFilterProp="children"
      notFoundContent={loading ? <Spin size="small" /> : 'No configs found'}
    >
      {configs.map((config) => (
        <Select.Option key={config.configName} value={config.configName}>
          {config.configName}
        </Select.Option>
      ))}
    </Select>
  );
};
